import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '../../utils/api';

const ADMIN = '/admin';

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function extractListPayload(payload, preferredKeys = []) {
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return [];

  const keysToCheck = [...preferredKeys, 'data', 'items', 'results', 'users'];
  for (const key of keysToCheck) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
    if (isPlainObject(value)) {
      const nested = extractListPayload(value, preferredKeys);
      if (nested.length > 0) return nested;
    }
  }

  return [];
}

function extractPaginationPayload(payload) {
  if (!isPlainObject(payload)) {
    return { page: 1, limit: 20, total: 0, totalPages: 0 };
  }

  const source = payload.pagination ?? payload.meta ?? payload;
  const page = Number(source.page ?? source.current_page ?? 1);
  const limit = Number(source.limit ?? source.per_page ?? source.page_size ?? 20);
  const total = Number(source.total ?? source.total_count ?? source.totalCount ?? 0);
  const totalPages = Number(
    source.totalPages ??
      source.total_pages ??
      (limit > 0 ? Math.ceil(total / limit) : 0)
  );

  return {
    page: Number.isFinite(page) ? page : 1,
    limit: Number.isFinite(limit) ? limit : 20,
    total: Number.isFinite(total) ? total : 0,
    totalPages: Number.isFinite(totalPages) ? totalPages : 0,
  };
}

// POST /admin/get-all-users (body: role, is_approved, is_active, include_inactive, is_email_verified, auth_provider, search, from_date, to_date, page, limit)
export const fetchUsers = createAsyncThunk(
  'user/fetchUsers',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${ADMIN}/get-all-users`, filters);
      return {
        users: extractListPayload(data, ['users']),
        pagination: extractPaginationPayload(data),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch users');
    }
  }
);

export const fetchUserById = createAsyncThunk(
  'user/fetchUserById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/get-user-by-id/${id}`);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch user');
    }
  }
);

export const createUser = createAsyncThunk(
  'user/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/create-user', userData);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to create user');
    }
  }
);

export const updateUser = createAsyncThunk(
  'user/updateUser',
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/update-user/${id}`, userData);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to update user');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'user/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/delete-user/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to delete user');
    }
  }
);

const initialState = {
  users: [],
  selectedUser: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    selectUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload?.users ?? [];
        state.pagination = action.payload?.pagination ?? state.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch users';
      })
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch user';
      })
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        const user = action.payload;
        if (user && typeof user === 'object') state.users.push(user);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create user';
      })
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (!payload?.id) return;
        const index = state.users.findIndex((u) => u.id === payload.id);
        if (index !== -1) state.users[index] = payload;
        if (state.selectedUser?.id === payload.id) {
          state.selectedUser = payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update user';
      })
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter((u) => u.id !== action.payload);
        if (state.selectedUser?.id === action.payload) {
          state.selectedUser = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete user';
      });
  },
});

export const { selectUser, clearError } = userSlice.actions;
export default userSlice.reducer;

export const selectUsers = (state) => state.user.users;
export const selectSelectedUser = (state) => state.user.selectedUser;
export const selectUserLoading = (state) => state.user.loading;
export const selectUserError = (state) => state.user.error;
export const selectUserPagination = (state) => state.user.pagination;

export function useUser() {
  const dispatch = useDispatch();
  const users = useSelector(selectUsers);
  const selectedUser = useSelector(selectSelectedUser);
  const loading = useSelector(selectUserLoading);
  const error = useSelector(selectUserError);
  const pagination = useSelector(selectUserPagination);

  return {
    users,
    selectedUser,
    loading,
    error,
    pagination,
    fetchUsers: useCallback((filters) => dispatch(fetchUsers(filters)), [dispatch]),
    fetchUserById: useCallback((id) => dispatch(fetchUserById(id)), [dispatch]),
    createUser: useCallback((userData) => dispatch(createUser(userData)), [dispatch]),
    updateUser: useCallback(({ id, userData }) => dispatch(updateUser({ id, userData })), [dispatch]),
    deleteUser: useCallback((id) => dispatch(deleteUser(id)), [dispatch]),
  };
}
